import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { fetcher } from '@/utils/fetcher';

export const generateReportReasonsQueryOptions = (type?: ConversationType) =>
  queryOptions({
    queryKey: [type === 'session' ? '/GetUserReasonList' : '/GetGroupReasonList'],
    queryFn: ({ signal }) =>
      fetcher<ListRespondedWith<ReportReason>>(
        type === 'session' ? '/GetUserReasonList' : '/GetGroupReasonList',
        undefined,
        {
          signal,
          schema: generateListResponseScheme(conversationReportReasonScheme),
          validResponseCodes: [0, 1],
        },
      ).then((response) => response?.Data ?? []),
    staleTime: 7 * 24 * 60 * 60 * 1000,
  });

export default function useReportReasons(type?: ConversationType) {
  const query = useQuery(generateReportReasonsQueryOptions(type));

  return {
    reasons: query.data ?? [],
    ...query,
  };
}

const conversationReportReasonScheme = z.object({
  ID: z.number(),
  Reason: z.string(),
});

type ReportReason = z.infer<typeof conversationReportReasonScheme>;
